<div class="ca-container">

  <!-- ── Toolbar ────────────────────────────────────────────────────────────── -->
  <div class="ca-toolbar">
    <span class="ca-title">VM Cost Analysis</span>

    <!-- Date window chips (visible once data is loaded) -->
    <ng-container *ngIf="costReport">
      <span class="ca-date-chip mat-caption">
        <mat-icon class="ca-date-icon">date_range</mat-icon>
        {{ formattedFromDate }} — {{ formattedToDate }}
      </span>
      <span class="ca-total-chip mat-body-2">
        <mat-icon class="ca-total-icon">payments</mat-icon>
        Total: {{ formatCost(costReport.grandTotal) }}
      </span>
    </ng-container>

    <button mat-stroked-button (click)="onRefreshClick()" [disabled]="isLoading">
      <mat-icon>refresh</mat-icon>{{ hasData ? 'Refresh' : 'Load' }}
    </button>

    <button mat-stroked-button (click)="exportExcel()" [disabled]="!hasData">
      <mat-icon>table_view</mat-icon> Export Excel
    </button>
  </div>

  <!-- ── Loading ────────────────────────────────────────────────────────────── -->
  <div *ngIf="isLoading" class="ca-state-row">
    <mat-progress-spinner mode="indeterminate" diameter="36"></mat-progress-spinner>
    <span>Loading VM cost data…</span>
  </div>

  <!-- ── Error ──────────────────────────────────────────────────────────────── -->
  <div *ngIf="!isLoading && loadError" class="ca-error-row">
    <mat-icon>error_outline</mat-icon>
    <span>{{ loadError }}</span>
    <button mat-stroked-button (click)="loadRequested.emit()">Retry</button>
  </div>

  <!-- ── Not loaded yet ─────────────────────────────────────────────────────── -->
  <div *ngIf="!isLoading && !loadError && !costReport" class="ca-empty">
    <mat-icon class="ca-empty-icon">attach_money</mat-icon>
    <p>
      No cost data available. Click <strong>Load</strong> to fetch Azure billing data
      scoped to each participant's resource group from the class start date to today.
    </p>
  </div>

  <!-- ── No cost rows ───────────────────────────────────────────────────────── -->
  <div *ngIf="!isLoading && !loadError && costReport && !hasData" class="ca-empty">
    <mat-icon class="ca-empty-icon">receipt_long</mat-icon>
    <p>No cost data found. Participant VMs may not be provisioned yet, or no costs
      have been incurred in the billing window.</p>
  </div>

  <!-- ── Main cost table ────────────────────────────────────────────────────── -->
  <ng-container *ngIf="!isLoading && !loadError && hasData">

    <div class="ca-table-wrap">
      <table mat-table [dataSource]="mainDataSource" multiTemplateDataRows class="ca-main-table">

        <!-- Row type badge -->
        <ng-container matColumnDef="rowType">
          <th mat-header-cell *matHeaderCellDef>Type</th>
          <td mat-cell *matCellDef="let row">
            <!-- Template row -->
            <span *ngIf="row.rowType === 'template'" class="ca-badge ca-badge-template">Template</span>
            <!-- Ad-hoc participant -->
            <span *ngIf="row.rowType === 'participant' && row.isAdhoc" class="type-chip type-chip-adhoc">
              <mat-icon class="chip-icon">badge</mat-icon> Ad-hoc
            </span>
            <!-- Email participant -->
            <span *ngIf="row.rowType === 'participant' && !row.isAdhoc" class="type-chip type-chip-email">
              <mat-icon class="chip-icon">alternate_email</mat-icon> Email
            </span>
          </td>
        </ng-container>

        <!-- Participant -->
        <ng-container matColumnDef="userName">
          <th mat-header-cell *matHeaderCellDef>Participant</th>
          <td mat-cell *matCellDef="let row" class="truncate-cell" [matTooltip]="row.loginId">
            {{ row.userName }}
          </td>
        </ng-container>

        <!-- Resource Group -->
        <ng-container matColumnDef="resourceGroup">
          <th mat-header-cell *matHeaderCellDef>Resource Group</th>
          <td mat-cell *matCellDef="let row" class="truncate-cell" [matTooltip]="row.resourceGroup">
            {{ row.resourceGroup }}
          </td>
        </ng-container>

        <!-- Subscription -->
        <ng-container matColumnDef="subscriptionName">
          <th mat-header-cell *matHeaderCellDef>Subscription</th>
          <td mat-cell *matCellDef="let row" class="truncate-cell" [matTooltip]="row.subscriptionId">
            {{ row.subscriptionName }}
          </td>
        </ng-container>

        <!-- VM / Ledger Status -->
        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef>Status</th>
          <td mat-cell *matCellDef="let row">
            <span class="ca-badge" [ngClass]="statusBadgeClass(row.status)">
              {{ statusLabel(row.status) }}
            </span>
          </td>
        </ng-container>

        <!-- Cost -->
        <ng-container matColumnDef="cost">
          <th mat-header-cell *matHeaderCellDef>Cost</th>
          <td mat-cell *matCellDef="let row">
            <div class="ca-cost-cell">
              {{ formatCost(row.cost) }}
              <mat-icon class="ca-expand-icon">
                {{ selectedRow === row ? 'expand_less' : 'expand_more' }}
              </mat-icon>
            </div>
          </td>
        </ng-container>

        <!-- Inline accordion detail row -->
        <ng-container matColumnDef="expandedDetail">
          <td mat-cell *matCellDef="let row" [attr.colspan]="mainColumns.length">
            <div class="ca-detail-wrapper" [@detailExpand]="selectedRow === row ? 'expanded' : 'collapsed'">

              <div *ngIf="!row.resources.length" class="ca-empty-detail">
                No resource-level cost data available for this resource group.
              </div>

              <div *ngIf="row.resources.length" class="ca-detail-inner">
                <table mat-table [dataSource]="selectedRow === row ? resourceDataSource : []" class="ca-detail-table">

                  <!-- Resource Type -->
                  <ng-container matColumnDef="name">
                    <th mat-header-cell *matHeaderCellDef>Type</th>
                    <td mat-cell *matCellDef="let r">
                      <span class="ca-badge ca-badge-type">{{ r.resourceTypeName || r.resourceType || '—' }}</span>
                    </td>
                  </ng-container>

                  <!-- Resource instance name from ledger -->
                  <ng-container matColumnDef="resourceId">
                    <th mat-header-cell *matHeaderCellDef>Name</th>
                    <td mat-cell *matCellDef="let r" class="truncate-cell" [matTooltip]="r.resourceId">
                      {{ r.resourceName || shortResourceId(r.resourceId) || '—' }}
                    </td>
                  </ng-container>

                  <!-- Cost -->
                  <ng-container matColumnDef="cost">
                    <th mat-header-cell *matHeaderCellDef>Cost</th>
                    <td mat-cell *matCellDef="let r">
                      <div class="ca-cost-cell">{{ formatCost(r.cost) }}</div>
                    </td>
                  </ng-container>

                  <tr mat-header-row *matHeaderRowDef="resourceColumns"></tr>
                  <tr mat-row *matRowDef="let r; columns: resourceColumns;"></tr>
                </table>
              </div>
            </div>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="mainColumns; sticky: true"></tr>
        <tr mat-row *matRowDef="let row; columns: mainColumns;" class="ca-main-row"
          [class.ca-row-selected]="selectedRow === row" (click)="selectRow(row)">
        </tr>
        <tr mat-row *matRowDef="let row; columns: ['expandedDetail']" class="ca-detail-row">
        </tr>

      </table>
    </div>

    <mat-paginator [pageSizeOptions]="pageSizeOptions" [pageSize]="pageSize" appPersistPageSize="class:costAnalysis:list" showFirstLastButtons>
    </mat-paginator>

  </ng-container><!-- /hasData -->

</div>
